/*   TWO TYPE OF OPTIONS:
 *
 *   without groups:
 *
 *   [
 *     { value: 500, label: 500},
 *     { value: 1500, label: 1500},
 *     { value: 2500, label: 2500},
 *   ]
 *
 *
 *  with groups:
 *
 *   [
 *     {
 *       label: 'Group 1',
 *       options: [{ value: 500, label: 500}]
 *     },
 *     {
 *       label: 'Group 2',
 *       options: [{ value: 1500, label: 1500}, { value: 2500, label: 2500}, ]
 *     },
 *   ];
 *
 *   */

import { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import elementType from 'prop-types-extra/lib/elementType';
import mapProps from 'map-props';
import cns from 'classnames';
import { noop, isObject, getOr } from 'lodash/fp';
import ChevronDown from 'components/icons/ChevronDownIcon.com.svg';
import CheckIcon from 'components/icons/CheckIconGreen.com.svg';

import ModalSelect from './ModalSelect';
import RefSwitcher from './RefSwitcher';
import propsMapping from './propsMapping';
import cn from './styles.local.less';

const nbsp = '\u00A0\u00A0';
const defaultValue = null;
const getValueFromEvent = (e) =>
  isObject(e) ? getOr(null, ['target', 'value'], e) : e;

const DefaultSelectedLabel = ({ label = null, className }) => (
  <div className={className}>{label}</div>
);

DefaultSelectedLabel.propTypes = {
  label: PropTypes.node,
  className: PropTypes.string,
};

class Control extends PureComponent {
  static propTypes = {
    DropdownIcon: elementType,
    OptionLabel: elementType,
    OptionModalLabel: elementType,
    autoFocus: PropTypes.bool,
    className: PropTypes.string,
    clearable: PropTypes.bool,
    disabled: PropTypes.bool,
    filterInputProps: PropTypes.object,
    flattenOptions: PropTypes.array,
    hasSelectedOptionValue: PropTypes.bool,
    input: PropTypes.object.isRequired,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    modalTitle: PropTypes.string,
    onChanged: PropTypes.func,
    options: PropTypes.array,
    placeholder: PropTypes.string,
    renderLabel: PropTypes.func,
    searchable: PropTypes.bool,
    selectedOption: PropTypes.object.isRequired,
    selectedOptionIndex: PropTypes.number,
    withMobile: PropTypes.bool,
    selectRef: PropTypes.func,
    qaSelector: PropTypes.string,
    mktSelector: PropTypes.string,
    withCheck: PropTypes.bool,
  };

  static defaultProps = {
    autoFocus: false,
    clearable: true,
    DropdownIcon: ChevronDown,
    onChanged: noop,
    flattenOptions: [],
    options: [],
    OptionLabel: DefaultSelectedLabel,
    OptionModalLabel: DefaultSelectedLabel,
    searchable: false,
    withMobile: true,
    selectRef: noop,
  };

  getValueByIndex(event) {
    const { flattenOptions } = this.props;
    const index = getValueFromEvent(event);

    return getOr(null, [index, 'value'], flattenOptions);
  }

  getRefOfSelect = (type) => {
    const { withMobile } = this.props;

    if (withMobile && type === 'mobile') {
      return this.refMobileSelect;
    }

    return this.refDesktopSelect;
  };

  changeValue = (value = defaultValue) => {
    const { input, onChanged } = this.props;

    input.onChange(value);

    onChanged(value);
  };

  handleSelectBlur = (event) => {
    const { input } = this.props;
    const value = this.getValueByIndex(event);

    input.onBlur(value);
  };

  handleSelectChange = (event) => {
    const value = this.getValueByIndex(event);

    this.changeValue(value);
  };

  handleResetButtonClick = () => {
    this.changeValue(defaultValue);
  };

  renderOptions() {
    const { options } = this.props;

    let indexOption = -1;

    return options.map((option) => {
      const { disabled, label, isGroup, value, isPlaceholder } = option;

      if (isGroup) {
        return (
          <Fragment key={label + indexOption}>
            <option disabled />
            {this.renderOption(value, label, (indexOption += 1), !value)}
            {option.options.map(({ value, label }) => {
              return (
                <option
                  data-qa-selector-value={value}
                  className={cn.option}
                  value={(indexOption += 1)}
                  key={label + indexOption}
                >
                  {nbsp} {label}
                </option>
              );
            })}
          </Fragment>
        );
      }

      return this.renderOption(
        value,
        label,
        (indexOption += 1),
        !!disabled,
        isPlaceholder,
      );
    });
  }

  renderOption(value, label, index, disabled = false, isPlaceholder) {
    return (
      <option
        data-qa-selector-value={value}
        disabled={disabled || isPlaceholder}
        className={cn.option}
        value={index}
        key={`${label} ${index}`}
      >
        {label}
      </option>
    );
  }

  render() {
    const {
      DropdownIcon,
      OptionLabel,
      OptionModalLabel,
      autoFocus,
      className,
      clearable,
      disabled,
      filterInputProps,
      flattenOptions,
      hasSelectedOptionValue,
      label,
      placeholder,
      searchable,
      selectedOption,
      selectedOptionIndex,
      modalTitle,
      withMobile,
      selectRef,
      qaSelector,
      mktSelector,
      withCheck,
    } = this.props;

    let { input } = this.props;

    if (qaSelector) {
      input = { ...input, 'data-qa-selector-value': qaSelector };
    }

    return (
      <div
        className={cns(
          cn.root,
          {
            [cn.disabled]: disabled,
          },
          className,
        )}
      >
        <select
          data-qa-selector="select"
          data-mkt-selector={mktSelector}
          {...input}
          autoFocus={autoFocus} // eslint-disable-line jsx-a11y/no-autofocus
          ref={(ref) => {
            this.refDesktopSelect = ref;
          }}
          value={selectedOptionIndex}
          className={cns(
            'form-control',
            { 'with-mobile': withMobile },
            cn.select,
          )}
          disabled={disabled}
          onBlur={this.handleSelectBlur}
          onChange={this.handleSelectChange}
        >
          {this.renderOptions()}
        </select>

        {withMobile ? (
          <ModalSelect
            disabled={disabled}
            OptionLabel={OptionLabel}
            OptionModalLabel={OptionModalLabel}
            filterInputProps={filterInputProps}
            label={label}
            onSelect={this.changeValue}
            options={flattenOptions}
            selectedOption={selectedOption}
            searchable={searchable}
            modalTitle={modalTitle}
            type={input.name}
            ref={(ref) => {
              this.refMobileSelect = ref;
            }}
          />
        ) : null}

        <OptionLabel
          {...selectedOption}
          className={cns(
            'form-control',
            cn.fakeSelectedOption,
            'select__selected-option',
            {
              [cn.isChecked]: hasSelectedOptionValue,
              [cn.placeholder]: selectedOption.isPlaceholder,
              'with-mobile': withMobile,
            },
          )}
        />

        {withCheck && hasSelectedOptionValue ? (
          <CheckIcon className={cn.checkIcon} />
        ) : (
          <DropdownIcon className={cn.icon} />
        )}

        <RefSwitcher
          getRefByType={this.getRefOfSelect}
          onDidMount={selectRef}
        />

        {clearable && hasSelectedOptionValue && placeholder && !disabled ? (
          <span
            className={cns(cn.reset, 'select__reset')}
            onClick={this.handleResetButtonClick}
          >
            ×
          </span>
        ) : null}
      </div>
    );
  }
}

export default mapProps(propsMapping)(Control);
